import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { DialogField } from '@/components/ui/dialog-form'
import { DialogPattern } from '@/components/ui/dialog-pattern'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useVerifyTimeEntryBillCurrencyMutation } from '@/hooks/mutations/useTimeEntryMutations'
import { CircleNotchIcon } from '@phosphor-icons/react'

interface BillCurrencyReviewDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  entryId: string
  expectedVersion: number
  onComplete: () => void
}

export function BillCurrencyReviewDialog({
  open,
  onOpenChange,
  entryId,
  expectedVersion,
  onComplete,
}: BillCurrencyReviewDialogProps) {
  const [currency, setCurrency] = useState('')
  const [sourceReference, setSourceReference] = useState('')
  const [reason, setReason] = useState('')
  const mutation = useVerifyTimeEntryBillCurrencyMutation()

  const resetAndClose = () => {
    setCurrency('')
    setSourceReference('')
    setReason('')
    onOpenChange(false)
  }
  const handleClose = () => {
    if (mutation.isPending) return
    resetAndClose()
  }

  const handleSubmit = async () => {
    if (
      !/^[A-Z]{3}$/.test(currency) ||
      !sourceReference.trim() ||
      !reason.trim()
    )
      return
    try {
      await mutation.mutateAsync({
        id: entryId,
        expectedVersion,
        currency,
        sourceReference: sourceReference.trim(),
        reason: reason.trim(),
      })
      resetAndClose()
      onComplete()
    } catch {
      // The mutation shows the server's refusal and retains the source text.
    }
  }

  return (
    <DialogPattern
      open={open}
      onOpenChange={handleClose}
      title="Review historical bill currency"
      description="Record the currency only after checking the original blended billing terms. The stored rate and amount will not change."
      contentClassName="space-y-4"
      footer={
        <>
          <Button
            type="button"
            variant="ghost"
            onClick={handleClose}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={
              mutation.isPending ||
              !/^[A-Z]{3}$/.test(currency) ||
              !sourceReference.trim() ||
              !reason.trim()
            }
          >
            {mutation.isPending && (
              <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
            )}
            Record currency
          </Button>
        </>
      }
    >
      <DialogField label="Bill currency" htmlFor="review-bill-currency">
        <Input
          id="review-bill-currency"
          autoComplete="off"
          maxLength={3}
          placeholder="USD"
          value={currency}
          onChange={event => setCurrency(event.target.value.toUpperCase())}
        />
      </DialogField>
      <DialogField label="Source reference" htmlFor="review-bill-source">
        <Input
          id="review-bill-source"
          autoComplete="off"
          maxLength={100}
          placeholder="Original project contract or rate record"
          value={sourceReference}
          onChange={event => setSourceReference(event.target.value)}
        />
      </DialogField>
      <DialogField label="Reason" htmlFor="review-bill-reason">
        <Textarea
          id="review-bill-reason"
          maxLength={1000}
          placeholder="Explain how the source establishes this currency for the work date"
          value={reason}
          onChange={event => setReason(event.target.value)}
        />
      </DialogField>
    </DialogPattern>
  )
}
